<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
    import { onMount } from 'svelte';
    export let height = 'auto';
    export let width = 'auto';

    let scrollableRoot = null;

    const availableScrollDistance = {
        left: null,
        right: null,
        top: null,
        bottom: null
    };

    $: shadowVisibility = {
        left: availableScrollDistance.left > 0,
        right: availableScrollDistance.right > 0,
        top: availableScrollDistance.top > 0,
        bottom: availableScrollDistance.bottom > 0
    };

    /**
     * Updates available scroll distance
     */
    function updateAvailableScrollDistance() {
        if (!scrollableRoot) {
            return;
        }

        const { scrollLeft, scrollTop, scrollWidth, scrollHeight } = scrollableRoot;
        const { clientWidth, clientHeight } = scrollableRoot;

        availableScrollDistance.left = Math.ceil(scrollLeft);
        availableScrollDistance.right = Math.floor(scrollWidth - clientWidth - scrollLeft);
        availableScrollDistance.top = Math.ceil(scrollTop);
        availableScrollDistance.bottom = Math.floor(scrollHeight - clientHeight - scrollTop);
    }

    onMount(() => {
        updateAvailableScrollDistance();
    });
</script>

<style>
    .scrollable-wrapper {
        overflow: auto;
        &:focus {
            outline: none;
        }
        &:focus-visible {
            @add-mixin simple-outline;
        }
    }

    .shadows {
        position: relative;
    }

    .shadow {
        --shadow-size: 1.5rem;
        --shadow-color: hsla(0, 0%, 12%, 25%);

        position: absolute;
        background-repeat: no-repeat;
        background-color: var(--color-bg-default-trans);
        z-index: var(--layer-1);

        &.left {
            left: 0;
            top: 0;
            width: var(--shadow-size);
            height: 100%;
            background: linear-gradient(to right, var(--shadow-color), var(--color-bg-default-trans));
        }
        &.right {
            right: 0;
            top: 0;
            width: var(--shadow-size);
            height: 100%;
            background: linear-gradient(to left, var(--shadow-color), var(--color-bg-default-trans));
        }
        &.top {
            left: 0;
            top: 0;
            height: var(--shadow-size);
            width: 100%;
            background: linear-gradient(to bottom, var(--shadow-color), var(--color-bg-default-trans));
        }
        &.bottom {
            left: 0;
            bottom: 0;
            height: var(--shadow-size);
            width: 100%;
            background: linear-gradient(to top, var(--shadow-color), var(--color-bg-default-trans));
        }
        &.hidden {
            opacity: 0;
        }
    }
</style>

<svelte:window on:resize={updateAvailableScrollDistance} />
<div
    class="shadows"
    style="height: {height}; width: {width};"
>
    {#each ['left', 'right', 'top', 'bottom'] as bound}
        <div class="shadow {bound}" class:hidden={!shadowVisibility[bound]} />
    {/each}
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div
        class="scrollable-wrapper"
        style="height: {height}; width: {width};"
        tabindex="0"
        bind:this={scrollableRoot}
        on:scroll={updateAvailableScrollDistance}
        on:scroll
        {...$$restProps}
    >
        <slot />
    </div>
</div>
